import React from 'react';
import { getBlockStyle, getTextStyle, generateTextStyleClasses, generateTextStyleInlineStyles } from 'utils/blockStyleUtils';
import TooltipWrapper from 'components/shared/TooltipWrapper';

/**
 * ReviewsBlock - Shared component for displaying teacher reviews
 *
 * Used in both edit mode (preview with sample data) and public mode (full reviews display).
 * Handles rating summary, distribution chart, and individual review cards.
 *
 * @param {Object} props
 * @param {Object} props.reviewsData - Reviews data object
 * @param {number} props.reviewsData.reviews_count - Total number of reviews
 * @param {number} props.reviewsData.average_rating - Average rating (0-5)
 * @param {Object} props.reviewsData.rating_distribution - Distribution object {5: count, 4: count, ...}
 * @param {Array} props.reviewsData.reviews - Array of review objects (public mode only)
 * @param {Object} props.translations - I18n translations object
 * @param {string} props.mode - Display mode: 'edit' (preview) or 'public' (full display)
 * @param {boolean} props.showSample - Show sample data when no reviews (edit mode)
 * @param {string} props.moreReviewsUrl - URL for "View all reviews" link (public mode)
 * @param {string} props.locale - Current locale (for date formatting)
 * @param {Object} props.pageDesign - Page design configuration for text styling
 */
export default function ReviewsBlock({
  reviewsData,
  translations,
  mode = 'public',
  showSample = false,
  moreReviewsUrl = null,
  locale = 'zh-TW',
  pageDesign
}) {
  const sampleData = translations?.inline_editor?.sample_data?.reviews || {};

  // Determine if we have real data
  const hasRealData = reviewsData && reviewsData.reviews_count > 0;
  const shouldShowSample = mode === 'edit' && showSample && !hasRealData;

  // Use real data or fallback to sample data
  const totalReviews = hasRealData ? reviewsData.reviews_count : (shouldShowSample ? 11 : 0);
  const averageRating = hasRealData ? Number(reviewsData.average_rating || 0) : (shouldShowSample ? 5.0 : 0);
  const ratingDistribution = hasRealData
    ? reviewsData.rating_distribution
    : (shouldShowSample ? { 5: 6, 4: 4, 3: 1, 2: 0, 1: 0 } : {});

  // Don't render if no data and not showing sample
  if (!hasRealData && !shouldShowSample) return null;

  // Get text styles
  const blockStyle = getBlockStyle(pageDesign, 'reviews');
  const titleTextStyle = getTextStyle(blockStyle, 'title');
  const contentTextStyle = getTextStyle(blockStyle, 'content');

  // Get tooltip text for review eligibility (mode-based: edit uses second-person, public uses third-person)
  const tooltipKey = mode === 'public'
    ? 'review_eligibility_tooltip_public'
    : 'review_eligibility_tooltip';
  const defaultTooltip = mode === 'public'
    ? 'To ensure review reliability, students must complete enough lessons before leaving a review'
    : 'To ensure review reliability, your students must complete enough lessons before leaving a review';
  const reviewTooltipText = translations?.[tooltipKey] || defaultTooltip;

  return (
    <div className={`reviews-block ${generateTextStyleClasses(contentTextStyle, pageDesign)}`} style={generateTextStyleInlineStyles(contentTextStyle)}>
      {/* Section Title */}
      {mode === 'edit' ? (
        <TooltipWrapper tooltip={reviewTooltipText}>
          <h2
            className={`font-semibold mb-6 ${generateTextStyleClasses(titleTextStyle, pageDesign)}`}
            style={generateTextStyleInlineStyles(titleTextStyle)}
          >
            ⭐ {translations.teacher_reviews?.teacher_page?.section_title || 'Student Reviews'} <span className="opacity-60">ℹ️</span>
          </h2>
        </TooltipWrapper>
      ) : (
        <h2
          className={`font-semibold mb-6 ${generateTextStyleClasses(titleTextStyle, pageDesign)}`}
          style={generateTextStyleInlineStyles(titleTextStyle)}
        >
          ⭐ {translations.teacher_reviews?.teacher_page?.section_title || 'Student Reviews'}
        </h2>
      )}

      {/* Rating Summary */}
      <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-lg p-6 mb-6 border border-yellow-200">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <div className="text-center">
              <div
                className={`font-bold ${generateTextStyleClasses(contentTextStyle, pageDesign)}`}
                style={generateTextStyleInlineStyles(contentTextStyle)}
              >
                {Number(averageRating || 0).toFixed(1)}
              </div>
              <div className="text-yellow-500 text-2xl mt-1">
                {[...Array(5)].map((_, i) => (
                  <span key={i}>{i < Math.round(averageRating) ? '★' : '☆'}</span>
                ))}
              </div>
            </div>
            <div>
              <div
                className={`font-medium ${generateTextStyleClasses(contentTextStyle, pageDesign)}`}
                style={generateTextStyleInlineStyles(contentTextStyle)}
              >
                {translations.teacher_reviews?.teacher_page?.average_rating || 'Average Rating'}
              </div>
              <div
                className={generateTextStyleClasses(contentTextStyle, pageDesign)}
                style={generateTextStyleInlineStyles(contentTextStyle)}
              >
                {(translations.teacher_reviews?.teacher_page?.based_on_reviews || 'Based on %{count} reviews')
                  .replace('%{count}', totalReviews)}
              </div>
            </div>
          </div>

          {/* Rating Distribution */}
          <div className="flex-1 min-w-[200px]">
            {[5, 4, 3, 2, 1].map(stars => {
              const count = ratingDistribution[stars] || 0;
              const percentage = totalReviews > 0 ? Math.round((count / totalReviews) * 100) : 0;
              return (
                <div key={stars} className="flex items-center gap-2 mb-1">
                  <span
                    className={`w-12 ${generateTextStyleClasses(contentTextStyle, pageDesign)}`}
                    style={generateTextStyleInlineStyles(contentTextStyle)}
                  >
                    {stars} ★
                  </span>
                  <div className="flex-1 bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-yellow-500 h-2 rounded-full"
                      style={{ width: `${percentage}%` }}
                    ></div>
                  </div>
                  <span
                    className={`w-8 text-right ${generateTextStyleClasses(contentTextStyle, pageDesign)}`}
                    style={generateTextStyleInlineStyles(contentTextStyle)}
                  >
                    {count}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Individual Reviews - Show in both edit and public modes */}
      <div className="space-y-4">
        {hasRealData && reviewsData.reviews?.length > 0 && reviewsData.reviews.map((review) => {
          const createdDate = new Date(review.created_at);
          const updatedDate = new Date(review.updated_at);
          const isEdited = (updatedDate - createdDate) > (5 * 60 * 1000); // 5 minutes
          const displayName = review.is_anonymous
            ? `${translations.teacher_reviews?.display?.anonymous_prefix || 'Student'} ${String.fromCharCode(65 + review.index)}`
            : review.student_name;

          return (
            <div key={review.id} className="bg-white border border-gray-200 rounded-lg p-6">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div
                    className={`font-medium ${generateTextStyleClasses(contentTextStyle, pageDesign)}`}
                    style={generateTextStyleInlineStyles(contentTextStyle)}
                  >
                    {displayName}
                  </div>
                  <div className="flex items-center mt-1">
                    <span className="text-yellow-500 text-lg">
                      {'★'.repeat(review.rating)}{'☆'.repeat(5 - review.rating)}
                    </span>
                    <span
                      className={`ml-2 ${generateTextStyleClasses(contentTextStyle, pageDesign)}`}
                      style={generateTextStyleInlineStyles(contentTextStyle)}
                    >
                      {(() => {
                        // Handle pluralization: based_on_classes can be an object {one: '...', other: '...'} or a string
                        const template = translations.teacher_reviews?.based_on_classes?.other
                          || translations.teacher_reviews?.based_on_classes
                          || 'Based on %{count} lessons';
                        return typeof template === 'string'
                          ? template.replace('%{count}', review.completed_classes_count)
                          : template;
                      })()}
                    </span>
                  </div>
                </div>
                <div
                  className={generateTextStyleClasses(contentTextStyle, pageDesign)}
                  style={generateTextStyleInlineStyles(contentTextStyle)}
                >
                  {createdDate.toLocaleDateString(
                    locale === 'zh-TW' ? 'zh-TW' :
                    locale === 'zh-CN' ? 'zh-CN' :
                    locale === 'ja' ? 'ja-JP' : 'en-US',
                    { year: 'numeric', month: '2-digit', day: '2-digit' }
                  )}
                  {isEdited && (
                    <span className="text-xs ml-2">
                      {translations.teacher_reviews?.display?.edited || 'Edited'}
                    </span>
                  )}
                </div>
              </div>
              {review.comment && (
                <p
                  className={`leading-relaxed ${generateTextStyleClasses(contentTextStyle, pageDesign)}`}
                  style={generateTextStyleInlineStyles(contentTextStyle)}
                >
                  {review.comment}
                </p>
              )}
            </div>
          );
        })}

        {/* Sample Review Card (Edit Mode) - Only show when no real reviews */}
        {mode === 'edit' && !hasRealData && (
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <div className="flex items-start justify-between mb-3">
              <div>
                <div
                  className={`font-medium ${generateTextStyleClasses(contentTextStyle, pageDesign)}`}
                  style={generateTextStyleInlineStyles(contentTextStyle)}
                >
                  {sampleData.student_name || 'Student A'}
                </div>
                <div className="flex items-center mt-1">
                  <span className="text-yellow-500 text-lg">
                    ★★★★☆
                  </span>
                  <span
                    className={`ml-2 ${generateTextStyleClasses(contentTextStyle, pageDesign)}`}
                    style={generateTextStyleInlineStyles(contentTextStyle)}
                  >
                    {(() => {
                      // Handle pluralization: based_on_classes can be an object {one: '...', other: '...'} or a string
                      const template = translations.teacher_reviews?.based_on_classes?.other
                        || translations.teacher_reviews?.based_on_classes
                        || 'Based on %{count} lessons';
                      return typeof template === 'string'
                        ? template.replace('%{count}', '5')
                        : template;
                    })()}
                  </span>
                </div>
              </div>
              <div
                className={generateTextStyleClasses(contentTextStyle, pageDesign)}
                style={generateTextStyleInlineStyles(contentTextStyle)}
              >
                {sampleData.date || '2025-01-01'}
              </div>
            </div>
            <p
              className={`leading-relaxed ${generateTextStyleClasses(contentTextStyle, pageDesign)}`}
              style={generateTextStyleInlineStyles(contentTextStyle)}
            >
              {sampleData.comment || 'The course content is practical and directly applicable to everyday life.'}
            </p>
          </div>
        )}
      </div>

      {/* Show More Button - Show in both modes when reviews > 2 */}
      {totalReviews > 2 && (
        <div className="text-center mt-6">
          {mode === 'public' && moreReviewsUrl ? (
            <a
              href={moreReviewsUrl}
              className="inline-flex items-center px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors duration-200 shadow-sm hover:shadow-md"
            >
              {(translations.teacher_reviews?.teacher_page?.view_all_reviews || 'View all %{count} reviews')
                .replace('%{count}', totalReviews)}
              <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"/>
              </svg>
            </a>
          ) : (
            <div className="inline-flex items-center px-6 py-3 bg-gray-400 text-white font-medium rounded-lg cursor-not-allowed opacity-75">
              {(translations.teacher_reviews?.teacher_page?.view_all_reviews || 'View all %{count} reviews')
                .replace('%{count}', totalReviews)}
              <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"/>
              </svg>
            </div>
          )}
        </div>
      )}

      {/* Sample Hint (Edit Mode) - Only show when no real reviews */}
      {mode === 'edit' && !hasRealData && (
        <p className="text-xs text-gray-500 mt-3 italic text-center">
          💡 {sampleData.description || 'Student reviews and ratings will appear here'}
        </p>
      )}
    </div>
  );
}
